/**
 * Which archetype flies where (03 §6, 10 §4). By construction rather than by retry:
 *  - each keystone is a keystone-eligible archetype native to its theatre, from the originals or the expansion;
 *  - every theatre then places three more natives, keeping at least two of the 1987 twelve among its native
 *    missions so the original spine survives;
 *  - slots left over take cross-terrain variants, never repeating an archetype in a theatre;
 *  - strict-quiet archetypes stay off the theatre's loud chain, and have first call on the slots beside it,
 *    which are the only ones they can take;
 *  - an archetype already flown in this operation is the last choice everywhere, and no theatre takes more
 *    than `categoryCap` missions of one category (demolition is a third of the list and most of the keystones).
 * Natives number 12 of at most 20 mission nodes (`MAP.maxMissionSlots` caps the rest): >= 60 % native.
 */

import type { Pcg32 } from '../../sim/rng';
import type { ArchetypeId, Biome } from '../../sim/types';
import type { MissionCategory } from '../content/archetypeFacts';
import { ARCHETYPES, type ArchetypeInfo, isOriginal } from '../content/archetypes';
import type { TheatreNode } from '../types';
import { shuffled, weighted } from '../util';
import type { Skeleton } from './graph';

/** Natives placed in a theatre besides its keystone, and how many of a theatre's natives must be originals. */
const NATIVES_PER_THEATRE = 3;
const ORIGINALS_PER_THEATRE = 2;
/**
 * Most missions of one category in a theatre, finale included: never more than half of it. And every theatre
 * spans at least `MIN_CATEGORIES` of the five, so its columns never read as one job six times over.
 */
export const categoryCap = (missions: number): number => (missions >= 6 ? 3 : 2);
export const MIN_CATEGORIES = 3;
/** Relative weights of a candidate's virtues; tuned against the 200-operation survey in meta3Opgen.test.ts. */
const WEIGHT = { fresh: 40, roomInCategory: [3, 2, 1], expansion: 2, strictBesideChain: 2 } as const;

/**
 * For a seat of choice the expansion outweighs the originals, who have the spine to themselves - less so a
 * mission that is also flown as its biome's finale, by the share of those finales that fall to it.
 */
function seatWeight(info: ArchetypeInfo): number {
  if (isOriginal(info)) return 1;
  const finales = ARCHETYPES.filter((a) => a.keystone && a.native === info.native).length;
  return WEIGHT.expansion * (info.keystone && finales > 0 ? 1 - 1 / finales : 1);
}

/** What one theatre holds so far. */
interface Picks {
  ids: Set<ArchetypeId>;
  categories: Map<MissionCategory, number>;
  /** The finale's category: it counts toward the cap, but not against the missions of choice that share it. */
  finale: MissionCategory | null;
  cap: number;
  /** Mission nodes of the theatre still to be given an archetype. */
  seats: number;
}

const countOf = (picks: Picks, info: ArchetypeInfo): number => picks.categories.get(info.facts.category) ?? 0;

/** A slot on the loud chain for a mission that may go there, else one beside it; strict-quiet only ever beside it. */
function takeSlot(free: TheatreNode[], chain: Set<string>, strict: boolean): TheatreNode | undefined {
  const beside = free.findIndex((n) => !chain.has(n.id));
  const on = free.findIndex((n) => chain.has(n.id));
  const index = strict ? beside : on >= 0 ? on : beside;
  return index < 0 ? undefined : free.splice(index, 1)[0];
}

/** Node id -> archetype for every mission node and keystone of the operation. */
export function assignArchetypes(
  rng: Pcg32, theatres: readonly Biome[], skeletons: readonly Skeleton[],
): Map<string, ArchetypeId> {
  const placed = new Map<string, ArchetypeId>();
  const uses = new Map<ArchetypeId, number>();

  /**
   * One of `options` by weight, or undefined when the theatre can take none of them. A mission already flown
   * in this operation is all but ruled out, and a category the theatre already has two of is unlikely. `how`:
   * `even` for a keystone or a seat of the spine; `open` for a seat of choice (`seatWeight`); `beside` for a
   * slot off the chain, where the strict-quiet missions outweigh the rest as well: they can fly nowhere else.
   */
  type How = 'even' | 'open' | 'beside';
  const pick = (options: readonly ArchetypeInfo[], picks: Picks, how: How): ArchetypeInfo | undefined => {
    // Short of `MIN_CATEGORIES` with only just enough seats left to reach it, only a new category will do.
    const mustBeNew = picks.categories.size + picks.seats - 1 < MIN_CATEGORIES;
    const open = options.filter((a) => !picks.ids.has(a.id) && countOf(picks, a) < picks.cap
      && !(mustBeNew && countOf(picks, a) > 0));
    if (open.length === 0) return undefined;
    return weighted(rng, open.map((a) => [a, (uses.has(a.id) ? 1 : WEIGHT.fresh)
      * (WEIGHT.roomInCategory[countOf(picks, a) - (picks.finale === a.facts.category ? 1 : 0)] ?? 1)
      * (how === 'even' ? 1 : seatWeight(a))
      * (how === 'beside' && a.strictQuiet ? WEIGHT.strictBesideChain : 1)] as const));
  };

  theatres.forEach((biome, t) => {
    const skeleton = skeletons[t];
    if (!skeleton) return;
    const missions = skeleton.nodes.filter((n) => n.kind === 'mission').length;
    const picks: Picks = {
      ids: new Set(), categories: new Map(), finale: null, cap: categoryCap(missions), seats: missions,
    };
    const place = (node: TheatreNode, info: ArchetypeInfo): void => {
      placed.set(node.id, info.id);
      uses.set(info.id, (uses.get(info.id) ?? 0) + 1);
      picks.ids.add(info.id);
      picks.categories.set(info.facts.category, countOf(picks, info) + 1);
      picks.seats -= 1;
    };

    const eligible = ARCHETYPES.filter((a) => a.keystone);
    const home = eligible.filter((a) => a.native === biome);
    const keystone = pick(home.length > 0 ? home : eligible, picks, 'even');
    const keystoneNode = skeleton.nodes.find((n) => n.id === skeleton.keystoneId);
    if (keystone && keystoneNode) place(keystoneNode, keystone);
    picks.finale = keystone?.facts.category ?? null;

    const free = shuffled(rng, skeleton.nodes.filter((n) => n.kind === 'mission' && !n.keystone));
    // Natives: the spine's seats first, so that nothing chosen later can crowd an original out, then the open
    // ones. A strict-quiet native is only chosen while a slot beside the chain is left for it, so none is ever
    // picked and then dropped.
    const natives = ARCHETYPES.filter((a) => a.native === biome);
    const spine = Math.max(0, ORIGINALS_PER_THEATRE - (keystone && isOriginal(keystone) ? 1 : 0));
    for (let n = 0; n < NATIVES_PER_THEATRE; n++) {
      const beside = free.some((node) => !skeleton.chain.has(node.id));
      const fits = natives.filter((a) => beside || !a.strictQuiet);
      const forSpine = n < spine;
      const info = (forSpine ? pick(fits.filter(isOriginal), picks, 'even') : undefined) ?? pick(fits, picks, 'open');
      const node = info ? takeSlot(free, skeleton.chain, info.strictQuiet) : undefined;
      if (!info || !node) break;
      place(node, info);
    }
    // Variants. The slots beside the chain go first: the strict-quiet missions can fly nowhere else, and
    // would otherwise be the rarest missions in the game.
    const rest = [...free].sort((a, b) => Number(skeleton.chain.has(a.id)) - Number(skeleton.chain.has(b.id)));
    for (const node of rest) {
      const loudOnly = skeleton.chain.has(node.id);
      const allowed = ARCHETYPES.filter((a) => !(loudOnly && a.strictQuiet));
      // A mission node never goes without a mission: if the mix rules leave nothing, they give way.
      const info = pick(allowed, picks, loudOnly ? 'open' : 'beside')
        ?? shuffled(rng, allowed.filter((a) => !picks.ids.has(a.id)))[0];
      if (info) place(node, info);
    }
  });
  return placed;
}
